document.addEventListener('DOMContentLoaded', () => {
  const stickyContainers = document.querySelectorAll('.sticky-atc');

  if (!('IntersectionObserver' in window) || stickyContainers.length === 0) {
    return;
  }

  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn(...args), delay);
    };
  };

  const getFirstCarouselImageSrc = (sectionRoot) => {
    const isVisible = (el) => el && el.offsetParent !== null;

    const customGalleries = [...sectionRoot.querySelectorAll('.custom-gallery')];
    const customGallery =
      customGalleries.find((gallery) => isVisible(gallery) && gallery.getAttribute('aria-hidden') !== 'true') ||
      customGalleries.find((gallery) => gallery.getAttribute('aria-hidden') !== 'true') ||
      customGalleries[0];

    if (customGallery) {
      const img = customGallery.querySelector('.product__media-item img');
      return img?.currentSrc || img?.src || null;
    }

    const gallery =
      [...sectionRoot.querySelectorAll('media-gallery')].find((el) => isVisible(el)) ||
      sectionRoot.querySelector('media-gallery');

    if (!gallery) return null;

    const firstSlide =
      gallery.querySelector('.product__media-item.is-active') ||
      gallery.querySelector('.product__media-item[data-media-id]:not([hidden])') ||
      gallery.querySelector('.product__media-item');
    const img = firstSlide?.querySelector('img');
    return img?.currentSrc || img?.src || null;
  };

  const initStickyProductImage = (container) => {
    const stickyImage = container.querySelector('.sticky-atc__product-image');
    if (!stickyImage) return;

    const sectionRoot = container.closest('.shopify-section') || container.closest('section');
    if (!sectionRoot) return;

    const setFirstCarouselImage = () => {
      const src = getFirstCarouselImageSrc(sectionRoot);
      if (!src) return;

      if (stickyImage.getAttribute('src') !== src && stickyImage.src !== src) {
        stickyImage.src = src;
      }

      // Keep srcset in sync so the browser doesn't prefer a stale responsive source.
      stickyImage.removeAttribute('srcset');
      stickyImage.removeAttribute('sizes');
    };

    setFirstCarouselImage();

    const carouselImg = sectionRoot.querySelector(
      '.custom-gallery .product__media-item img, media-gallery .product__media-item img'
    );
    if (carouselImg && !carouselImg.complete) {
      carouselImg.addEventListener('load', setFirstCarouselImage, { once: true });
    }

    const syncImage = debounce(setFirstCarouselImage, 50);

    // Watch Alpine x-show wrappers that toggle subscription vs one-time galleries.
    const observeVisibility = (el) => {
      if (!el) return;
      new MutationObserver(syncImage).observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
      });
    };

    sectionRoot.querySelectorAll('.product__media-wrapper').forEach((wrapper) => {
      observeVisibility(wrapper);
      [...wrapper.children].forEach(observeVisibility);
    });
    sectionRoot.querySelectorAll('media-gallery, .custom-gallery').forEach(observeVisibility);

    sectionRoot.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      if (
        target.matches(
          'input[name="purchase_type"], input[type="radio"], .sticky-atc__selector-native, select'
        )
      ) {
        // Wait a tick so Alpine can swap the visible gallery first.
        window.requestAnimationFrame(() => syncImage());
      }
    });
  };

  const initCustomSelector = (container) => {
    const selector = container.querySelector('.sticky-atc__selector');
    const nativeSelect = container.querySelector('.sticky-atc__selector-native');
    if (!selector || !nativeSelect) return;

    let panel = selector.querySelector('.sticky-atc__selector-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'sticky-atc__selector-panel';
      panel.hidden = true;
      selector.appendChild(panel);
    }

    const closePanel = () => {
      panel.hidden = true;
      selector.classList.remove('is-open');
    };

    const rebuildPanel = () => {
      panel.innerHTML = '';
      [...nativeSelect.options].forEach((option) => {
        const optionButton = document.createElement('button');
        optionButton.type = 'button';
        optionButton.className = 'sticky-atc__selector-option';
        if (option.selected) optionButton.classList.add('is-selected');

        const nameEl = document.createElement('span');
        nameEl.className = 'sticky-atc__selector-option-name';
        nameEl.textContent = option.dataset.name || option.textContent;

        const priceEl = document.createElement('span');
        priceEl.className = 'sticky-atc__selector-option-price';
        priceEl.textContent = option.dataset.price || '';

        optionButton.append(nameEl, priceEl);
        optionButton.addEventListener('click', (event) => {
          event.stopPropagation();
          nativeSelect.value = option.value;
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          closePanel();
        });
        panel.appendChild(optionButton);
      });
    };

    selector.classList.add('sticky-atc__selector--custom');
    rebuildPanel();

    selector.addEventListener('click', (event) => {
      if (event.target.closest('.sticky-atc__selector-option')) return;
      event.preventDefault();
      event.stopPropagation();
      if (panel.hidden) {
        rebuildPanel();
        panel.hidden = false;
        selector.classList.add('is-open');
      } else {
        closePanel();
      }
    });

    document.addEventListener('click', (event) => {
      if (!selector.contains(event.target)) closePanel();
    });

    new MutationObserver(debounce(rebuildPanel, 50)).observe(nativeSelect, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['selected', 'data-name', 'data-price'],
    });
  };

  stickyContainers.forEach((container) => {
    initStickyProductImage(container);
    initCustomSelector(container);

    const targetSelector = container.dataset.target;
    const targetButton = document.querySelector(targetSelector);
    if (!targetButton) {
      return;
    }

    const stickyButton = container.querySelector('.sticky-atc__button');
    if (!stickyButton) return;

    const syncButtonState = () => {
      const copiedClasses = [...targetButton.classList].filter(
        (cls) => cls !== 'button--full-width'
      );
      stickyButton.className = ['sticky-atc__button', ...copiedClasses].join(' ');
      stickyButton.disabled = targetButton.disabled;

      const targetSpinner = targetButton.querySelector('.loading__spinner');
      const stickySpinner = stickyButton.querySelector('.loading__spinner');
      if (targetSpinner && stickySpinner) {
        stickySpinner.className = targetSpinner.className;
      }

      if (targetButton.hasAttribute('aria-disabled')) {
        stickyButton.setAttribute('aria-disabled', targetButton.getAttribute('aria-disabled'));
      } else {
        stickyButton.removeAttribute('aria-disabled');
      }

      if (!stickyButton.querySelector('[x-text]')) {
        const stickyLabel = stickyButton.querySelector('span:not(.loading__spinner)');
        const targetLabel = targetButton.querySelector('span:not(.loading__spinner)');
        if (stickyLabel && targetLabel && !stickyLabel.hasAttribute('x-text')) {
          stickyLabel.innerHTML = targetLabel.innerHTML;
        }
      }
    };

    syncButtonState();

    stickyButton.addEventListener('click', () => {
      if (targetButton.disabled || targetButton.getAttribute('aria-disabled') === 'true') return;
      targetButton.click();
      syncButtonState();
    });

    const mutationObserver = new MutationObserver(syncButtonState);
    mutationObserver.observe(targetButton, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    const isHomepage = document.body.classList.contains('template-index');
    let scrollAllowsSticky = false;
    let footerPastHalfway = false;

    const updateStickyState = () => {
      // Respect hidden attribute (set by waitlist feature when product is sold out)
      if (container.hasAttribute('hidden')) {
        container.classList.remove('active');
        return;
      }
      if (scrollAllowsSticky && !footerPastHalfway) {
        container.classList.add('active');
      } else {
        container.classList.remove('active');
      }
    };

    const footer = document.querySelector('footer.footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(
        ([entry]) => {
          // Hide sticky once at least half the footer is in view.
          footerPastHalfway = entry.intersectionRatio >= 0.5;
          updateStickyState();
        },
        {
          threshold: [0, 0.5, 1],
        }
      );
      footerObserver.observe(footer);
    }

    if (isHomepage) {
      let mainButtonHidden = false;
      let sectionReached = false;

      const syncHomepageScroll = () => {
        scrollAllowsSticky = sectionReached && mainButtonHidden;
        updateStickyState();
      };

      const heroSection = document.querySelector('#MainContent > .shopify-section:first-child');

      const buttonObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          mainButtonHidden = !entry.isIntersecting;
          syncHomepageScroll();
        });
      });

      if (heroSection) {
        const heroObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              sectionReached = false;
            } else {
              sectionReached = entry.boundingClientRect.top < 0;
            }
            syncHomepageScroll();
          });
        }, { threshold: 0.55 });
        heroObserver.observe(heroSection);
      }

      buttonObserver.observe(targetButton);
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          scrollAllowsSticky = !entry.isIntersecting;
          updateStickyState();
        });
      });

      io.observe(targetButton);
    }
  });
});
